import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { UserAreaModel } from '../../../models/user-area.model';
import { AlertService } from '../../../services/alert.service';
import { UserAreaService } from '../../../services/user-area.service';
import { ImageCropperDialogComponent } from '../image-cropper-dialog/image-cropper-dialog.component';

@Component({
  selector: 'app-edit-member-area',
  templateUrl: './edit-member-area.component.html',
  styleUrl: './edit-member-area.component.scss',
  standalone: false,
})
export class EditMemberAreaComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  public form: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.maxLength(500)]),
  });

  public isLoading = false;
  public area: UserAreaModel;
  public selectedImage: string | null = null;
  private imageFile: File | null = null;

  constructor(
    private userAreaService: UserAreaService,
    private alertService: AlertService,
    private dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private dialogService: DialogService
  ) {
    this.area = this.config.data?.area;
    if (this.area) {
      this.form.patchValue({
        name: this.area.name,
        description: this.area.description,
      });

      if (this.area.image) {
        this.selectedImage = URL.createObjectURL(this.area.image);
      }
    }
  }

  handleImageUpload(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;
    const file = input.files[0];

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.alertService.showErrorAlert(
        'Ungültiger Dateityp. Bitte lade nur Bilder im Format JPEG, JPG, PNG oder WEBP hoch.'
      );
      input.value = '';
      return;
    }

    this.openImageCropperDialog(event);
  }

  private openImageCropperDialog(event: Event): void {
    const dialogRef = this.dialogService.open(ImageCropperDialogComponent, {
      header: 'Bild zuschneiden',
      width: '50vw',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        imageChangedEvent: event,
        aspectRatio: 1 / 1,
        roundCropper: false,
        height: 800,
        containWithinAspectRatio: true,
        onlyScaleDown: false,
        format: 'png',
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((result: any) => {
      if (!result) return;

      // Handle File
      if (result instanceof File) {
        this.imageFile = result;
        this.selectedImage = URL.createObjectURL(result);
        return;
      }

      // Handle base64 string
      if (typeof result === 'string') {
        this.selectedImage = result;
        this.imageFile = this.dataUrlToFile(result, 'member-area.webp');
        return;
      }

      // Handle object with file or dataUrl
      if (result.file instanceof File) {
        this.imageFile = result.file;
        this.selectedImage = URL.createObjectURL(result.file);
      } else if (typeof result.dataUrl === 'string') {
        this.selectedImage = result.dataUrl;
        this.imageFile = this.dataUrlToFile(result.dataUrl, 'member-area.webp');
      }
    });
  }

  private dataUrlToFile(dataUrl: string, fileName: string): File {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/webp';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isLoading = true;
    this.userAreaService
      .updateUserArea(
        this.area.id,
        {
          name: this.form.get('name')?.value,
          description: this.form.get('description')?.value,
        },
        this.imageFile
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.alertService.showSuccessAlert('Bereich aktualisiert.');
          this.dialogRef.close(true);
        },
        error: err => {
          this.isLoading = false;
          this.alertService.showErrorAlert(
            err?.message || 'Aktualisierung fehlgeschlagen.'
          );
        },
      });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getCharacterCount(): string {
    const current = this.form.get('description')?.value?.length || 0;
    return `${current} / 500`;
  }
}
